/**
 * Capability-based permissions vocabulary (Doc 05, permission refactor).
 *
 * Runtime store: role_permissions.permission holds these capability IDs.
 * No new role_capabilities table; no registry YAML as runtime truth.
 *
 * See DOCS_V3/03-implementation/capability-based-permissions-implementation-plan.md
 */

import type { ActorRole } from './roles';
import type { PermissionAction } from './permissions';
import { ACTOR_ROLE_PERMISSIONS } from './actorRolePermissions';
import { DEFAULT_JOB_ROLES } from './jobRoles';

// ============================================================================
// Canonical capability IDs (stored in role_permissions.permission)
// ============================================================================

export type CapabilityId =
  // Cycle
  | 'CYCLE_VIEW'
  | 'CYCLE_PARTICIPATE'
  | 'CYCLE_CREATE'
  | 'CYCLE_CONFIGURE'
  | 'CYCLE_ABORT'
  | 'PHASE_ADVANCE'
  // Track
  | 'TRACK_CREATE'
  | 'TRACK_CONFIGURE'
  // Items
  | 'ITEM_VIEW'
  | 'ITEM_CRUD'
  | 'ITEM_TRIAGE_APPROVE'
  | 'ITEM_TRIAGE_ROUTE'
  | 'ITEM_TRIAGE_ATTACH_RELATIONSHIPS'
  | 'ITEM_MANAGE_OWNERSHIP'
  | 'ITEM_APPROVE_AI_PROVENANCE'
  // Criteria & scales
  | 'CRITERIA_SELECT'
  | 'CRITERIA_LOCK'
  | 'SCALES_DEFINE'
  | 'REOPEN_CRITERIA'
  // Value range
  | 'VALUE_RANGE_DEFINE'
  | 'VALUE_RANGE_APPROVE'
  // Values
  | 'VALUE_ENTER'
  | 'VALUE_ACCEPT'
  | 'VALUE_REJECT'
  | 'VALUES_CONVERGE'
  // Pool
  | 'POOL_SELECT'
  | 'POOL_LOCK'
  | 'REOPEN_POOL'
  // Session & Event
  | 'SESSION_CREATE'
  | 'SESSION_RUN'
  | 'SESSION_PARTICIPATE'
  | 'EVENT_DESIGN'
  // Ranking
  | 'RANK_RUN'
  | 'RESULTS_VIEW'
  // Override & legitimacy
  | 'OVERRIDE_APPLY'
  // S-33 (mig 891): "Change ranking order" — separate from OVERRIDE_APPLY ("Override values")
  | 'RANK_OVERRIDE'
  | 'LEGITIMACY_ADVANCE'
  // Catchball
  | 'CATCHBALL_REQUEST_ISSUE'
  | 'CATCHBALL_PROPOSAL_SUBMIT'
  | 'CATCHBALL_RETURN'
  | 'CATCHBALL_ACCEPT_PROVISIONAL'
  | 'CATCHBALL_ACCEPT'
  | 'CATCHBALL_REJECT'
  // Admin
  | 'ADMIN_MANAGE_USERS'
  | 'ADMIN_MANAGE_ROLES'
  | 'ADMIN_MANAGE_PERMISSIONS'
  | 'ADMIN_GRANT_WORKSPACE'
  | 'ADMIN_GRANT_TENANT'
  // Workflow (Stage 6 — configurable from Settings)
  | 'WORKFLOW_CREATE'
  | 'WORKFLOW_EDIT'
  | 'WORKFLOW_DELETE'
  | 'WORKFLOW_ARCHIVE'
  | 'WORKFLOW_SET_DEFAULT'
  | 'WORKFLOW_ASSIGN'
  | 'WORKFLOW_USE'
  | 'WORKFLOW_MANAGE_TEMPLATES'
  // Tasks
  | 'TASK_ASSIGN'
  | 'TASK_SELF_ASSIGN';

/** All capability IDs in a single array (source of truth for "all" e.g. platform-admin). */
export const CANONICAL_CAPABILITY_IDS: readonly CapabilityId[] = [
  'CYCLE_VIEW',
  'CYCLE_PARTICIPATE',
  'CYCLE_CREATE',
  'CYCLE_CONFIGURE',
  'CYCLE_ABORT',
  'PHASE_ADVANCE',
  'TRACK_CREATE',
  'TRACK_CONFIGURE',
  'ITEM_VIEW',
  'ITEM_CRUD',
  'ITEM_TRIAGE_APPROVE',
  'ITEM_TRIAGE_ROUTE',
  'ITEM_TRIAGE_ATTACH_RELATIONSHIPS',
  'ITEM_MANAGE_OWNERSHIP',
  'ITEM_APPROVE_AI_PROVENANCE',
  'CRITERIA_SELECT',
  'CRITERIA_LOCK',
  'SCALES_DEFINE',
  'REOPEN_CRITERIA',
  'VALUE_RANGE_DEFINE',
  'VALUE_RANGE_APPROVE',
  'VALUE_ENTER',
  'VALUE_ACCEPT',
  'VALUE_REJECT',
  'VALUES_CONVERGE',
  'POOL_SELECT',
  'POOL_LOCK',
  'REOPEN_POOL',
  'SESSION_CREATE',
  'SESSION_RUN',
  'SESSION_PARTICIPATE',
  'EVENT_DESIGN',
  'RANK_RUN',
  'RESULTS_VIEW',
  'OVERRIDE_APPLY',
  'RANK_OVERRIDE',
  'LEGITIMACY_ADVANCE',
  'CATCHBALL_REQUEST_ISSUE',
  'CATCHBALL_PROPOSAL_SUBMIT',
  'CATCHBALL_RETURN',
  'CATCHBALL_ACCEPT_PROVISIONAL',
  'CATCHBALL_ACCEPT',
  'CATCHBALL_REJECT',
  'ADMIN_MANAGE_USERS',
  'ADMIN_MANAGE_ROLES',
  'ADMIN_MANAGE_PERMISSIONS',
  'ADMIN_GRANT_WORKSPACE',
  'ADMIN_GRANT_TENANT',
  'WORKFLOW_CREATE',
  'WORKFLOW_EDIT',
  'WORKFLOW_DELETE',
  'WORKFLOW_ARCHIVE',
  'WORKFLOW_SET_DEFAULT',
  'WORKFLOW_ASSIGN',
  'WORKFLOW_USE',
  'WORKFLOW_MANAGE_TEMPLATES',
  'TASK_ASSIGN',
  'TASK_SELF_ASSIGN',
] as const;

export function isCapabilityId(s: string): s is CapabilityId {
  return (CANONICAL_CAPABILITY_IDS as readonly string[]).includes(s);
}

// ============================================================================
// Role name → capability bundle (for backfill and fallback when DB has no rows)
// Admin roles: derived from ACTOR_ROLE_PERMISSIONS + PERMISSION_ACTION_TO_CAPABILITIES (see below).
// Keys: ActorRole / roles.name
// ============================================================================

// ROLE_CAPABILITY_BUNDLES is defined after PERMISSION_ACTION_TO_CAPABILITIES so derivation can use it.

/**
 * Explicit mapping from DB role name or display_name variants to ROLE_CAPABILITY_BUNDLES key.
 * Use this for backfill so we never rely on guesswork. Add any DB-specific names here.
 */
export const DB_ROLE_NAME_TO_BUNDLE_KEY: Record<string, string> = {
  'workspace-admin': 'workspace-admin',
  'tenant-admin': 'tenant-admin',
  'platform-admin': 'platform-admin',
  'system-admin': 'system-admin',
  'Workspace Administrator': 'workspace-admin',
  'Workspace admin': 'workspace-admin',
  'Workspace Admin': 'workspace-admin',
  'Tenant Administrator': 'tenant-admin',
  'Tenant admin': 'tenant-admin',
  'Platform Administrator': 'platform-admin',
  'Platform admin': 'platform-admin',
  'workspace_admin': 'workspace-admin',
  'tenant_admin': 'tenant-admin',
  // Short-form role names used in role_assignments and workspace_memberships
  'admin': 'workspace-admin',
  'owner': 'workspace-admin',
  'Admin': 'workspace-admin',
  'Owner': 'workspace-admin',
  'administrator': 'workspace-admin',
  'Administrator': 'workspace-admin',
};

function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/**
 * Resolve DB role name (and optional display_name) to the bundle key used in ROLE_CAPABILITY_BUNDLES.
 * Prefer explicit DB_ROLE_NAME_TO_BUNDLE_KEY; fallback: lowercase trim, then slug(display_name).
 */
export function getBundleKeyForDbRole(dbName: string, displayName?: string | null): string | null {
  const name = (dbName ?? '').trim();
  const display = (displayName ?? '').trim();
  if (DB_ROLE_NAME_TO_BUNDLE_KEY[name]) return DB_ROLE_NAME_TO_BUNDLE_KEY[name];
  const nameLower = name.toLowerCase();
  if (ROLE_CAPABILITY_BUNDLES[nameLower]) return nameLower;
  if (display && DB_ROLE_NAME_TO_BUNDLE_KEY[display]) return DB_ROLE_NAME_TO_BUNDLE_KEY[display];
  if (display) {
    const slugged = slug(display);
    if (ROLE_CAPABILITY_BUNDLES[slugged]) return slugged;
  }
  return null;
}

/**
 * Get capability bundle for a role by name (ActorRole or roles.name).
 * Used for backfill and as fallback when role_permissions has no rows for the role.
 */
export function getCapabilitiesForRole(roleName: string | null | undefined): CapabilityId[] {
  if (!roleName) return [];
  const normalized = roleName.trim().toLowerCase();
  const bundle = ROLE_CAPABILITY_BUNDLES[normalized] ?? ROLE_CAPABILITY_BUNDLES[roleName as ActorRole];
  return bundle ? [...bundle] : [];
}

/**
 * Resolve a list of role names to a single set of capability IDs (union of all role bundles).
 * Use this once at the start of permission evaluation; all runtime checks then use capabilities only.
 */
export function resolveUserCapabilities(roleIds: string[]): CapabilityId[] {
  const set = new Set<CapabilityId>();
  for (const roleId of roleIds) {
    const caps = getCapabilitiesForRole(roleId);
    for (const c of caps) set.add(c);
  }
  return [...set];
}

/**
 * Get capability bundle for a DB role (id, name, display_name). Uses explicit mapping when available.
 */
export function getCapabilitiesForDbRole(role: {
  id: string;
  name: string | null;
  display_name?: string | null;
}): { bundleKey: string; capabilities: CapabilityId[] } | null {
  const key = getBundleKeyForDbRole(role.name ?? '', role.display_name);
  if (!key) return null;
  const capabilities = ROLE_CAPABILITY_BUNDLES[key];
  return capabilities ? { bundleKey: key, capabilities: [...capabilities] } : null;
}

// ============================================================================
// PermissionAction → CapabilityId (backward compatibility)
// ============================================================================

/** Map each PermissionAction to one or more capability IDs required to perform it. */
export const PERMISSION_ACTION_TO_CAPABILITIES: Record<PermissionAction, CapabilityId[]> = {
  // Items
  'items.view': ['ITEM_VIEW'],
  'items.create': ['ITEM_CRUD'],
  'items.triage': ['ITEM_CRUD'],
  // Granular triage sub-permissions: dedicated capability OR legacy ITEM_CRUD satisfies each.
  // This keeps backwards compatibility — existing roles with ITEM_CRUD can still do everything.
  // Clients wanting fine-grained control can grant only ITEM_TRIAGE_* without ITEM_CRUD.
  'items.triage.approve': ['ITEM_TRIAGE_APPROVE', 'ITEM_CRUD'],
  'items.triage.route': ['ITEM_TRIAGE_ROUTE', 'ITEM_CRUD'],
  'items.triage.attach_relationships': ['ITEM_TRIAGE_ATTACH_RELATIONSHIPS', 'ITEM_CRUD'],
  'items.manage_ownership': ['ITEM_MANAGE_OWNERSHIP', 'ITEM_CRUD'],
  'items.edit': ['ITEM_CRUD'],
  'items.delete': ['ITEM_CRUD'],
  'items.approve_ai_provenance': ['ITEM_APPROVE_AI_PROVENANCE'],
  'relationships.create': ['ITEM_CRUD'],
  'relationships.edit': ['ITEM_CRUD'],
  'relationships.delete': ['ITEM_CRUD'],
  // Process / Cycle
  'process.view': ['CYCLE_VIEW'],
  'process.create': ['CYCLE_CREATE'],
  'process.edit': ['CYCLE_CONFIGURE'],
  'process.prioritize': ['RANK_RUN'],
  'process.publish': ['LEGITIMACY_ADVANCE'],
  'process.advance_phase': ['PHASE_ADVANCE'],
  'process.legitimacy_advance': ['LEGITIMACY_ADVANCE'],
  'cycle.pool.add': ['POOL_SELECT'],
  'cycle.pool.remove': ['POOL_SELECT'],
  'cycle.abort': ['CYCLE_ABORT'],
  // Tracks
  'tracks.create': ['TRACK_CREATE'],
  'tracks.configure': ['TRACK_CONFIGURE'],
  'track.set_official_result': ['CYCLE_CONFIGURE'],
  // Sessions & Events
  'session.view': ['CYCLE_VIEW', 'RESULTS_VIEW'],
  'session.facilitate': ['SESSION_RUN'],
  'session.record': ['SESSION_RUN'],
  'session.participate': ['SESSION_PARTICIPATE'],
  'session.vote': ['SESSION_PARTICIPATE', 'RANK_RUN'],
  'session.pool': ['POOL_SELECT'],
  'session.pool.add': ['POOL_SELECT'],
  'session.pool.remove': ['POOL_SELECT'],
  'session.publish': ['LEGITIMACY_ADVANCE'],
  'session.run_ranking': ['RANK_RUN'],
  'session.view_results': ['RESULTS_VIEW'],
  'event.design': ['EVENT_DESIGN'],
  'ranking.override': ['RANK_OVERRIDE'],
  // Pools
  'pools.select': ['POOL_SELECT'],
  'pools.lock': ['POOL_LOCK'],
  'pools.reopen': ['REOPEN_POOL'],
  // Lists / Item Pools
  'lists.view': ['RESULTS_VIEW', 'ITEM_VIEW'],
  'lists.create': ['POOL_SELECT'],
  'lists.edit': ['POOL_SELECT', 'ITEM_CRUD'],
  'lists.move_cutline': ['POOL_SELECT', 'RANK_RUN'],
  'lists.publish': ['LEGITIMACY_ADVANCE'],
  // Criteria
  'criteria.view': ['CYCLE_VIEW', 'CRITERIA_SELECT'],
  'criteria.create': ['SCALES_DEFINE'],
  'criteria.edit': ['SCALES_DEFINE', 'CRITERIA_SELECT'],
  'criteria.delete': ['SCALES_DEFINE'],
  'criteria.select': ['CRITERIA_SELECT'],
  'criteria.weight': ['CRITERIA_SELECT'],
  'criteria.lock': ['CRITERIA_LOCK'],
  'criteria.reopen': ['REOPEN_CRITERIA'],
  // Values & Value Ranges
  'values.propose': ['VALUE_ENTER'],
  'values.accept': ['VALUE_ACCEPT'],
  'values.reject': ['VALUE_REJECT'],
  'values.override': ['OVERRIDE_APPLY'],
  'values.define_range': ['VALUE_RANGE_DEFINE'],
  'values.approve_range': ['VALUE_RANGE_APPROVE'],
  'values.define_scales': ['SCALES_DEFINE'],
  'values.converge': ['VALUES_CONVERGE'],
  // Attributes
  'attributes.define': ['SCALES_DEFINE', 'VALUE_RANGE_DEFINE'],
  'attributes.assign_owner': ['VALUE_RANGE_APPROVE'],
  // Type lists (migration 931) — the database decides (internal_actor_may_edit_type_list)
  'type_lists.edit': ['SCALES_DEFINE'],
  // Catchball
  'catchball.request': ['CATCHBALL_REQUEST_ISSUE'],
  'catchball.propose': ['CATCHBALL_PROPOSAL_SUBMIT'],
  'catchball.return': ['CATCHBALL_RETURN'],
  'catchball.accept_provisional': ['CATCHBALL_ACCEPT_PROVISIONAL'],
  'catchball.accept': ['CATCHBALL_ACCEPT'],
  'catchball.reject': ['CATCHBALL_REJECT'],
  // Analytics
  'analytics.view': ['RESULTS_VIEW'],
  'analytics.export': ['RESULTS_VIEW'],
  // Workflow
  'workflow.create': ['WORKFLOW_CREATE'],
  'workflow.edit': ['WORKFLOW_EDIT'],
  'workflow.delete': ['WORKFLOW_DELETE'],
  'workflow.archive': ['WORKFLOW_ARCHIVE'],
  'workflow.set_default': ['WORKFLOW_SET_DEFAULT'],
  'workflow.assign': ['WORKFLOW_ASSIGN'],
  'workflow.use': ['WORKFLOW_USE'],
  'workflow.manage_templates': ['WORKFLOW_MANAGE_TEMPLATES'],
  // Tasks
  'task.assign': ['TASK_ASSIGN', 'SESSION_RUN'],
  'task.self_assign': ['TASK_SELF_ASSIGN', 'SESSION_PARTICIPATE'],
  // Admin
  'admin.users': ['ADMIN_MANAGE_USERS'],
  'admin.groups': ['ADMIN_MANAGE_USERS'],
  'admin.roles': ['ADMIN_MANAGE_ROLES'],
  'admin.policies': ['ADMIN_MANAGE_PERMISSIONS'],
  'admin.settings': ['ADMIN_MANAGE_PERMISSIONS'],
  'admin.grant_workspace': ['ADMIN_GRANT_WORKSPACE'],
  'admin.grant_tenant': ['ADMIN_GRANT_TENANT'],
};

/**
 * Derive capability IDs from a list of permission actions.
 * Permissions with no mapping contribute nothing.
 */
function deriveCapabilitiesFromPermissions(permissions: string[]): CapabilityId[] {
  const set = new Set<CapabilityId>();
  for (const p of permissions) {
    const caps = PERMISSION_ACTION_TO_CAPABILITIES[p as PermissionAction];
    if (caps) for (const c of caps) set.add(c);
  }
  return [...set];
}

/** Baseline permissions auto-granted via workspace-member (migration 475). */
const WORKSPACE_MEMBER_BASELINE: PermissionAction[] = [
  'items.view',
  'process.view',
  'session.view',
  'session.participate',
  'lists.view',
  'analytics.view',
  'task.self_assign',
];

function buildJobRoleCapabilityBundles(): Record<string, CapabilityId[]> {
  const bundles: Record<string, CapabilityId[]> = {};
  for (const role of DEFAULT_JOB_ROLES) {
    bundles[role.slug] = deriveCapabilitiesFromPermissions([
      ...WORKSPACE_MEMBER_BASELINE,
      ...role.permissions,
    ]);
  }
  return bundles;
}

/** Admin role capability bundles derived from ACTOR_ROLE_PERMISSIONS + PERMISSION_ACTION_TO_CAPABILITIES. */
export const ROLE_CAPABILITY_BUNDLES: Record<string, CapabilityId[]> = {
  // Admin roles — all capabilities derived from their permission action lists.
  'workspace-admin': deriveCapabilitiesFromPermissions(ACTOR_ROLE_PERMISSIONS['workspace-admin']),
  'tenant-admin': deriveCapabilitiesFromPermissions(ACTOR_ROLE_PERMISSIONS['tenant-admin']),
  'platform-admin': deriveCapabilitiesFromPermissions(ACTOR_ROLE_PERMISSIONS['platform-admin']),
  'system-admin': deriveCapabilitiesFromPermissions(ACTOR_ROLE_PERMISSIONS['system-admin']),

  // Baseline for every workspace member (DB: migration 475).
  'workspace-member': deriveCapabilitiesFromPermissions(WORKSPACE_MEMBER_BASELINE),

  // Default job roles — baseline + delta (fallback when DB role_permissions missing).
  ...buildJobRoleCapabilityBundles(),
};

/**
 * Resolve a PermissionAction to the set of capability IDs that grant it.
 * User needs at least one of these capabilities (for requireAny) or all (for requireAll).
 */
export function getCapabilitiesForAction(action: PermissionAction): CapabilityId[] {
  return PERMISSION_ACTION_TO_CAPABILITIES[action] ?? [];
}

/** Inverse: which PermissionActions are granted by a capability (for getEffectivePermissions). */
const _capabilityToActions = new Map<CapabilityId, PermissionAction[]>();
(function buildCapabilityToActions() {
  for (const [action, caps] of Object.entries(PERMISSION_ACTION_TO_CAPABILITIES)) {
    for (const cap of caps) {
      const list = _capabilityToActions.get(cap) ?? [];
      list.push(action as PermissionAction);
      _capabilityToActions.set(cap, list);
    }
  }
})();

export function getActionsGrantedByCapability(capabilityId: CapabilityId): PermissionAction[] {
  return _capabilityToActions.get(capabilityId) ?? [];
}
